import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useEffect, lazy, Suspense } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { NotificationService } from './services/NotificationService';
import OfflineIndicator from './components/OfflineIndicator';

// Layout
import AppLayout from './components/AppLayout';

// Pages - Auth
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));

// Pages - Main
const ChatsPage = lazy(() => import('./pages/chats/ChatsPage'));
const ChatWindowPage = lazy(() => import('./pages/chats/ChatWindowPage'));
const ShopFeedPage = lazy(() => import('./pages/shops/ShopFeedPage'));
const ShopPage = lazy(() => import('./pages/shops/ShopPage'));
const CommunitiesPage = lazy(() => import('./pages/communities/CommunitiesPage'));
const CommunityPage = lazy(() => import('./pages/communities/CommunityPage'));
const OrdersPage = lazy(() => import('./pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const SubscribePage = lazy(() => import('./pages/profile/SubscribePage'));
const SettingsPage = lazy(() => import('./pages/profile/SettingsPage'));
const PrivacyPage = lazy(() => import('./pages/profile/PrivacyPage'));
const RateAppPage = lazy(() => import('./pages/profile/RateAppPage'));
const HelpSupportPage = lazy(() => import('./pages/profile/HelpSupportPage'));

// Pages - Dashboard (Shop Owner)
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ShopSettingsPage = lazy(() => import('./pages/dashboard/ShopSettingsPage'));
const ProductsManagerPage = lazy(() => import('./pages/dashboard/ProductsManagerPage'));
const CustomerCRMPage = lazy(() => import('./pages/dashboard/CustomerCRMPage'));
const ParchiManagerPage = lazy(() => import('./pages/dashboard/ParchiManagerPage'));
const BroadcastPage = lazy(() => import('./pages/dashboard/BroadcastPage'));
const LedgerPage = lazy(() => import('./pages/dashboard/LedgerPage'));
const ReportsPage = lazy(() => import('./pages/dashboard/ReportsPage'));

// Pages - Onboarding
const CreateShopPage = lazy(() => import('./pages/onboarding/CreateShopPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

// PWA & Public Pages
const ProductPublicPage = lazy(() => import('./pages/shops/ProductPublicPage'));
const FavouritesPage = lazy(() => import('./pages/shops/FavouritesPage'));
const MarketSearchPage = lazy(() => import('./pages/shops/MarketSearchPage'));

function ProtectedRoute({ children }) {
  const { isAuth, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="loading-spinner" /></div>;
  return isAuth ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { isAuth, loading } = useAuth();
  if (loading) return null;
  return !isAuth ? children : <Navigate to="/shops" replace />;
}

function AppRoutes() {
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Helper to route deep links from native intents to React Router routes
    const handleDeepLink = (urlOrPath) => {
      if (!urlOrPath) return;
      console.log('[DeepLink] Processing:', urlOrPath);

      let path = urlOrPath;

      // Extract path if it contains custom protocol or scheme (e.g. shopaxo://chat/123 or https://...)
      if (urlOrPath.includes(':/')) {
        const schemeEndIndex = urlOrPath.indexOf(':/');
        const rest = urlOrPath.substring(schemeEndIndex + 2).replace(/^\/+/, '');
        
        if (urlOrPath.startsWith('http')) {
          const slashIndex = rest.indexOf('/');
          path = slashIndex !== -1 ? rest.substring(slashIndex) : '/';
        } else {
          path = '/' + rest;
        }
      }

      // Normalize native paths to match plural React Router paths
      let targetPath = path;
      if (path.startsWith('/chat/')) {
        targetPath = path.replace('/chat/', '/chats/');
      } else if (path.startsWith('/order/')) {
        targetPath = path.replace('/order/', '/orders/');
      } else if (path.startsWith('/customer/')) {
        targetPath = path.replace('/customer/', '/dashboard/customers/');
      } else if (path.startsWith('/product/')) {
        targetPath = path.replace('/product/', '/dashboard/products/');
      } else if (path.startsWith('/ledger/')) {
        const id = path.split('/').pop();
        targetPath = `/dashboard/ledger?userId=${id}`;
      }

      console.log('[DeepLink] Navigating React app to:', targetPath);
      navigate(targetPath);
    };

    // 2. Handle cold boot deep link if MainActivity passed it via sessionStorage
    const pendingNav = sessionStorage.getItem('db_pending_navigation');
    if (pendingNav) {
      sessionStorage.removeItem('db_pending_navigation');
      console.log('[DeepLink] Found pending navigation in sessionStorage:', pendingNav);
      handleDeepLink(pendingNav);
    }

    if (Capacitor.isNativePlatform()) {
      NotificationService.init(navigate);

      // Register Capacitor listener for deep links (warm/cold starts while app is active)
      const urlListener = CapApp.addListener('appUrlOpen', (event) => {
        console.log('[DeepLink] appUrlOpen event received:', event.url);
        handleDeepLink(event.url);
      });

      // Hardware back button handler
      const backListener = CapApp.addListener('backButton', (data) => {
        const path = window.location.pathname;
        const exitPaths = ['/chats', '/shops', '/dashboard', '/communities', '/orders', '/profile', '/login', '/register', '/'];
        if (exitPaths.includes(path)) {
          CapApp.exitApp();
        } else {
          if (data.canGoBack && window.history.state && window.history.state.idx > 0 && window.history.length > 1) {
            navigate(-1);
          } else {
            // Fallback navigation based on path
            if (path.startsWith('/chats/')) {
              navigate('/chats');
            } else if (path.startsWith('/communities/')) {
              navigate('/communities');
            } else if (path.startsWith('/orders/')) {
              navigate('/orders');
            } else if (path.startsWith('/shops/')) {
              navigate('/shops');
            } else if (path.startsWith('/dashboard/')) {
              if (path === '/dashboard/ledger') {
                navigate('/dashboard/customers');
              } else {
                navigate('/dashboard');
              }
            } else if (path === '/favourites') {
              navigate('/profile');
            } else if (path === '/market') {
              navigate('/shops');
            } else if (path === '/subscribe') {
              navigate('/profile');
            } else if (path === '/start-shop') {
              navigate('/profile');
            } else {
              navigate('/shops');
            }
          }
        }
      });

      // App resume event for session check
      const resumeListener = CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          console.log('App resumed. Checking session integrity...');
          // Check for any deep links generated while app was suspended
          const resumePending = sessionStorage.getItem('db_pending_navigation');
          if (resumePending) {
            sessionStorage.removeItem('db_pending_navigation');
            handleDeepLink(resumePending);
          }
        }
      });

      return () => {
        urlListener.then(h => h.remove());
        backListener.then(h => h.remove());
        resumeListener.then(h => h.remove());
      };
    }
  }, [navigate]);

  return (
    <>
      <OfflineIndicator />
      <Suspense fallback={
        <div className="flex items-center justify-center h-screen">
          <div className="loading-spinner" />
        </div>
      }>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/p/:productId" element={<ProductPublicPage />} />

          {/* Protected Routes with App Layout */}
          <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route path="chats" element={<ChatsPage />} />
            <Route path="chats/:id" element={<ChatWindowPage />} />
            <Route path="shops" element={<ShopFeedPage />} />
            <Route path="shops/:slug" element={<ShopPage />} />
            <Route path="favourites" element={<FavouritesPage />} />
            <Route path="market" element={<MarketSearchPage />} />
            <Route path="communities" element={<CommunitiesPage />} />
            <Route path="communities/:id" element={<CommunityPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/:id" element={<OrderDetailPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="subscribe" element={<SubscribePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="rate" element={<RateAppPage />} />
            <Route path="help" element={<HelpSupportPage />} />
            <Route path="start-shop" element={<CreateShopPage />} />

            {/* Dashboard Routes */}
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="dashboard/settings" element={<ShopSettingsPage />} />
            <Route path="dashboard/products" element={<ProductsManagerPage />} />
            <Route path="dashboard/customers" element={<CustomerCRMPage />} />
            <Route path="dashboard/parchis" element={<ParchiManagerPage />} />
            <Route path="dashboard/broadcasts" element={<BroadcastPage />} />
            <Route path="dashboard/ledger" element={<LedgerPage />} />
            <Route path="dashboard/reports" element={<ReportsPage />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ChatProvider>
          <AppRoutes />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: '#16161F',
                color: '#F0F0F8',
                border: '1px solid #2A2A38',
                borderRadius: '12px',
                fontSize: '14px',
              },
              success: { iconTheme: { primary: '#22C55E', secondary: '#fff' } },
              error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
            }}
          />
        </ChatProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
